import * as React from 'react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

/** Status icons take the status colour. Class names are written out in full so Tailwind finds them. */
const statusIcon = {
  success: '[&_[data-icon]]:text-[var(--color-success)]',
  error: '[&_[data-icon]]:text-[var(--color-danger)]',
  warning: '[&_[data-icon]]:text-[var(--color-warning)]',
  info: '[&_[data-icon]]:text-[var(--color-info)]',
} as const;

/** A faint status tint over the opaque card colour, with a status border and normal text. */
const status = (name: string, token: string) => ({
  [`--${name}-bg`]: `linear-gradient(var(--color-${token}-tint), var(--color-${token}-tint)) var(--color-card)`,
  [`--${name}-border`]: `var(--color-${token})`,
  [`--${name}-text`]: 'var(--color-card-foreground)',
});

/**
 * Sonner draws the toast from its own CSS variables, and its stylesheet beats
 * Tailwind classes, so the CGuard tokens go in through those variables. This
 * keeps toasts on the current theme (light, dark, high contrast).
 */
const tokens = {
  '--normal-bg': 'var(--color-card)',
  '--normal-bg-hover': 'var(--color-muted)',
  '--normal-border': 'var(--color-border)',
  '--normal-border-hover': 'var(--color-border)',
  '--normal-text': 'var(--color-card-foreground)',
  '--gray1': 'var(--color-card)',
  '--gray2': 'var(--color-muted)',
  '--gray4': 'var(--color-border)',
  '--gray5': 'var(--color-border)',
  '--gray12': 'var(--color-card-foreground)',
  ...status('success', 'success'),
  ...status('error', 'danger'),
  ...status('warning', 'warning'),
  ...status('info', 'info'),
} as React.CSSProperties;

/** Keeps toasts clear of the top bar and of a sticky bottom action bar. */
const offset = { top: '4.5rem', bottom: '5rem' };

/**
 * Toast surface — wraps `sonner` with CGuard token defaults so SPAs do not
 * have to thread theme details. Mount `<Toaster />` once at the SPA root.
 * `position` defaults to top-right and stays a prop (operator: bottom-right,
 * commissioning: top-center).
 */
function Toaster({
  style,
  ...props
}: React.ComponentProps<typeof SonnerToaster>): React.JSX.Element {
  return (
    <SonnerToaster
      closeButton
      richColors
      position="top-right"
      offset={offset}
      mobileOffset={offset}
      style={{ ...tokens, ...style }}
      toastOptions={{ classNames: statusIcon }}
      {...props}
    />
  );
}

export { Toaster, sonnerToast as toast };
